import {
  getDeleteTextRequest,
  getInsertTextRequest,
} from './BatchReport.helpers';

import {
  SalesPerSubscriberLiftByDate,
  SalesPerSubscriberOverallLift,
  AovLiftByDate,
  AovOverallLift,
  FrequencyLiftByDate,
  FrequencyOverallLift,
} from '../ReportDetail/scenes/ControlGroupReports';

import {
  PerformanceOverview,
  PerformanceOverviewBySegment,
  TransactionsByCashbackPercentage,
  TransactionsBySegment,
  TransactionVolume,
  RepeatVisitsByCashbackPercentage,
  RepeatVisitsBySegment,
  SubscriberPurchasePatterns,
  SubscriberPurchasePatternsBySegment,
  TopMetroTransactions,
  TopMetroAov,
  TopMetroSales,
  OnlineOfflineTransactions,
  Demographics,
  YearOverYear,
} from '../ReportDetail/scenes/PerformanceReports';

import {
  SafegraphVisitorShare,
  SafegraphLapsedUser,
} from '../ReportDetail/scenes/ThirdPartyReports';

type ReportReference = {
  [key: string]: {
    props: Record<string, unknown>;
    Component: React.ElementType;
  };
};

type Queries = string[] | string;

type BatchConfigData = {
  path: string;
  formatter: (value: any) => string;
  defaultValue: number | null;
  containsText: string;
};

type BatchConfigSegmentedData = {
  idx: number;
  replaceTextConfig: BatchConfigData[];
  isSegmented: boolean;
  segmentValue: number | string | null;
  startDate?: string;
  endDate?: string;
};

export type ReportConfig = {
  [key: string]: {
    pageId: string;
    name: string;
    queries: Queries[];
    additionalOptions?: Record<string, unknown>;
    imageName?: string;
  };
};

const topMetroAdditionalOptions = {
  size: {
    width: {
      magnitude: 600,
      unit: 'PT',
    },
    height: {
      magnitude: 500,
      unit: 'PT',
    },
  },
  transform: {
    translateX: 50,
    translateY: -30,
    scaleX: 1.15,
    scaleY: 1.15,
  },
};

const reportsConfig: ReportConfig = {
  performanceOverview: {
    pageId: 'p2',
    name: 'Performance Overview',
    imageName: 'performance_overview',
    queries: ['merchantTransactionTotals'],
    additionalOptions: {
      size: {
        width: {
          magnitude: 408.96,
          unit: 'PT',
        },
        height: {
          magnitude: 416.88,
          unit: 'PT',
        },
      },
      transform: {
        translateX: 310.32,
        translateY: -5.76,
      },
    },
  },
  performanceOverviewBySegment: {
    pageId: 'gf436a943a5_0_0',
    name: 'Performance Overview By Segment',
    imageName: 'performance_overview_by_segment',
    queries: ['merchantTransactionTotals'],
    additionalOptions: {
      size: {
        width: {
          magnitude: 408.96,
          unit: 'PT',
        },
        height: {
          magnitude: 416.88,
          unit: 'PT',
        },
      },
      transform: {
        translateX: 310.32,
        translateY: -5.76,
      },
    },
  },
  transactions: {
    pageId: 'p3',
    name: 'Transactions',
    imageName: 'transactions',
    queries: ['merchantTransactionSummary'],
  },
  transactionsBySegment: {
    pageId: 'gf436a943a5_0_21',
    name: 'Transactions By Segment',
    imageName: 'transactions_by_segment',
    queries: ['merchantTransactionSummaryBySegment'],
  },
  salesYearOverYear: {
    pageId: 'g13530202113_0_0',
    name: 'Merchant Sales Year Over Year',
    imageName: 'sales_yoy',
    queries: ['merchantPerformanceYearOverYear'],
  },
  transactionsYearOverYear: {
    pageId: 'g13530202113_0_8',
    name: 'Merchant Transactions Year Over Year',
    imageName: 'transactions_yoy',
    queries: ['merchantPerformanceYearOverYear'],
  },
  transactionsYearOverYearByVenueType: {
    pageId: 'g1344f241929_0_0',
    name: 'Merchant Transactions Year Over Year By Venue Type',
    imageName: 'transactions_yoy_by_venuetype',
    queries: ['merchantPerformanceYearOverYear'],
  },
  salesYearOverYearByVenueType: {
    pageId: 'g13530202113_0_16',
    name: 'Merchant Sales Year Over Year  By Venue Type',
    imageName: 'sales_yoy_by_venue_type',
    queries: ['merchantPerformanceYearOverYear'],
  },
  onlineOfflineTransactions: {
    pageId: 'p4',
    name: 'Transactions: Online/In-Store',
    imageName: 'transactions_online_instore',
    queries: [['transactionSummary', 'result']],
  },
  transactionVolume: {
    pageId: 'p5',
    name: 'Transactions Volume',
    imageName: 'transactions_volume',
    queries: [['transactionSummary', 'result']],
  },
  repeatVisits: {
    pageId: 'p7',
    name: 'Repeat Visits',
    imageName: 'repeat_visits',
    queries: ['purchasesBySubWithMulPurchases', 'percentNewTransactors'],
  },
  repeatVisitsBySegment: {
    pageId: 'gf436a943a5_0_39',
    name: 'Repeat Visits By Segment',
    imageName: 'repeat_visits_by_segment',
    queries: [
      'purchasesBySubWithMulPurchasesBySegment',
      'percentNewTransactorsBySegment',
    ],
  },
  subscriberPurchasePatterns: {
    pageId: 'p8',
    name: 'Subscriber Purchase Patterns',
    imageName: 'subscriber_purchase_patterns',
    queries: ['purchasesBySubWithMulPurchases'],
  },
  subscriberPurchasePatternsBySegment: {
    pageId: 'gf436a943a5_0_30',
    name: 'Subscriber Purchase Patterns By Segment',
    imageName: 'subscriber_purchase_patterns_by_segment',
    queries: ['purchasesBySubWithMulPurchasesBySegment'],
  },
  topMetroTransactions: {
    pageId: 'p9',
    name: 'Top Metro Transactions',
    imageName: 'top_metro_transactions',
    additionalOptions: topMetroAdditionalOptions,
    queries: [['metricsByMetro', 'topMetrosByTransactionCount']],
  },
  topMetroAOV: {
    pageId: 'p10',
    name: 'Top Metro AOV',
    imageName: 'top_metro_AOV',
    additionalOptions: topMetroAdditionalOptions,
    queries: [['metricsByMetro', 'topMetrosByAov']],
  },
  topMetroSales: {
    pageId: 'p11',
    name: 'Top Metro Sales',
    imageName: 'top_metro_sales',
    additionalOptions: topMetroAdditionalOptions,
    queries: [['metricsByMetro', 'topMetrosByRevenue']],
  },
  shopperDemographics: {
    pageId: 'p14',
    name: 'Shopper Demographics',
    imageName: 'shopper_demographics',
    queries: ['demographicSegments'],
    additionalOptions: {
      size: {
        width: {
          magnitude: 650,
          unit: 'PT',
        },
        height: {
          magnitude: 591,
          unit: 'PT',
        },
      },
      transform: {
        translateX: 50,
        translateY: -25,
      },
    },
  },
  safegraphVisitorShare: {
    pageId: 'p15',
    name: 'Safegraph Visitor Share',
    imageName: 'safegraph_visitor_share',
    queries: ['safegraphVisitorShare'],
  },
  safegraphLapsedUser: {
    pageId: 'p16',
    name: 'Safegraph Lapsed User',
    imageName: 'safegraph_lapsed_user',
    queries: ['lapsedUser'],
  },
  salesPerSubscriberLiftByDate: {
    pageId: 'p17',
    name: 'Sales Per Subscriber (Lift by Date)',
    imageName: 'sales_per_subscriber_lift_by_date',
    queries: [['controlGroup', 'liftResults']],
  },
  salesPerSubscriberOverallLift: {
    pageId: 'p18',
    name: 'Sales Per Subscriber (Overall Lift)',
    imageName: 'sales_per_subscriber_overall_lift',
    queries: [['controlGroup', 'salesPerSubscriber']],
  },
  aovLiftByDate: {
    pageId: 'p19',
    name: 'AOV (Lift by Date)',
    imageName: 'aov_lift_by_date',
    queries: [['controlGroup', 'liftResults']],
  },
  aovOverallLift: {
    pageId: 'p20',
    name: 'AOV (Overall Lift)',
    imageName: 'aov_overall_lift',
    queries: [['controlGroup', 'rawAov']],
  },
  frequencyLiftByDate: {
    pageId: 'p21',
    name: 'Frequency (Lift by Date)',
    imageName: 'frequency_lift_by_date',
    queries: [['controlGroup', 'liftResults']],
  },
  frequencyOverallLift: {
    pageId: 'p22',
    name: 'Frequency (Overall Lift)',
    imageName: 'frequency_overall_lift',
    queries: [['controlGroup', 'indexedFrequency']],
  },
};

const reportReference: ReportReference = {
  topMetroTransactions: {
    props: {},
    Component: TopMetroTransactions,
  },
  topMetroAOV: {
    props: {},
    Component: TopMetroAov,
  },
  topMetroSales: {
    props: {},
    Component: TopMetroSales,
  },
  performanceOverview: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: PerformanceOverview,
  },
  performanceOverviewBySegment: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: PerformanceOverviewBySegment,
  },
  salesYearOverYear: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: YearOverYear,
  },
  salesYearOverYearByVenueType: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: YearOverYear,
  },
  repeatVisits: {
    props: { height: 800 },
    Component: RepeatVisitsByCashbackPercentage,
  },
  repeatVisitsBySegment: {
    props: { height: 800 },
    Component: RepeatVisitsBySegment,
  },
  subscriberPurchasePatterns: {
    props: { height: 800 },
    Component: SubscriberPurchasePatterns,
  },
  subscriberPurchasePatternsBySegment: {
    props: { height: 800 },
    Component: SubscriberPurchasePatternsBySegment,
  },
  transactions: {
    props: { height: 800 },
    Component: TransactionsByCashbackPercentage,
  },
  transactionsYearOverYear: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: YearOverYear,
  },
  transactionsYearOverYearByVenueType: {
    props: { height: 800, extraClasses: { batchMode: true } },
    Component: YearOverYear,
  },
  transactionsBySegment: {
    props: { height: 800 },
    Component: TransactionsBySegment,
  },
  onlineOfflineTransactions: {
    props: { height: 800 },
    Component: OnlineOfflineTransactions,
  },
  transactionVolume: {
    props: { height: 800 },
    Component: TransactionVolume,
  },
  safegraphVisitorShare: {
    props: { height: 600 },
    Component: SafegraphVisitorShare,
  },
  safegraphLapsedUser: {
    props: { isAnimationActive: false, height: 600 },
    Component: SafegraphLapsedUser,
  },
  salesPerSubscriberLiftByDate: {
    props: { height: 650 },
    Component: SalesPerSubscriberLiftByDate,
  },
  salesPerSubscriberOverallLift: {
    props: { height: 700 },
    Component: SalesPerSubscriberOverallLift,
  },
  aovLiftByDate: {
    props: { height: 650 },
    Component: AovLiftByDate,
  },
  aovOverallLift: {
    props: { height: 700 },
    Component: AovOverallLift,
  },
  frequencyLiftByDate: {
    props: { height: 650 },
    Component: FrequencyLiftByDate,
  },
  frequencyOverallLift: {
    props: { height: 700 },
    Component: FrequencyOverallLift,
  },
  shopperDemographics: {
    props: { isAnimationActive: false },
    Component: Demographics,
  },
};

const merchantNameObjectId = 'p1_i61';
const dateRangeObjectId = 'p1_i62';

const merchantUpdateStyleRequest = {
  updateTextStyle: {
    objectId: merchantNameObjectId,
    style: {
      fontFamily: 'Montserrat',
      fontSize: {
        magnitude: 48,
        unit: 'PT',
      },
      bold: true,
    },
    fields: 'fontFamily,fontSize,bold',
  },
};

const dateRangeUpdateStyleRequest = {
  updateTextStyle: {
    objectId: dateRangeObjectId,
    style: {
      fontFamily: 'Montserrat',
      fontSize: {
        magnitude: 20,
        unit: 'PT',
      },
    },
    fields: 'fontFamily,fontSize',
  },
};

const textShapeRequestsConfig = (merchantName: string, dateRange: string) => [
  getDeleteTextRequest(merchantNameObjectId),
  getDeleteTextRequest(dateRangeObjectId),
  getInsertTextRequest(merchantNameObjectId, merchantName),
  getInsertTextRequest(dateRangeObjectId, dateRange),
  merchantUpdateStyleRequest,
  dateRangeUpdateStyleRequest,
];

export {
  BatchConfigData,
  BatchConfigSegmentedData,
  reportsConfig,
  textShapeRequestsConfig,
  reportReference,
};
